/**
 * TerrainHeightBlend — height-aware layer blend function.
 *
 * Replaces linear lerp with material interpenetration: taller surface features
 * poke through at transition boundaries, producing natural layering (e.g. rocks
 * emerging from soil, snow accumulating in crevices).
 *
 * Two versions are provided:
 *   - `heightBlend`     — CPU mirror, usable in tests and CPU weight baking.
 *   - `tslHeightBlend`  — TSL Fn node for GPU materials.
 *
 * Algorithm (Perez et al. — "Terrain and Occlusion Culling with Heightmaps"):
 *
 *   raw = (w0 - w1) * sharpness + (h0 - h1)
 *   t   = smoothstep(clamp(raw, 0, 1))
 *
 * A positive raw value means layer 0 wins; negative means layer 1 wins.
 * `sharpness` amplifies weight differences so that even a small weight
 * advantage causes a decisive blend at high sharpness values (4–16 typical).
 * The height term allows layer 1 to intrude into layer 0's territory when its
 * height field is locally higher — simulating material interpenetration.
 */

import { Fn, float, clamp, smoothstep } from 'three/tsl';

// ---------------------------------------------------------------------------
// CPU mirror (for tests and baking)
// ---------------------------------------------------------------------------

/**
 * heightBlend — CPU implementation of height-aware layer blending.
 *
 * Centred at `raw == 0` so equal weights + equal heights yield a 50/50
 * blend. The previous `clamp(raw, 0, 1)` window snapped any non-positive
 * raw to pure layer 1, meaning two equal layers silently collapsed to
 * only layer 1 — surprising for callers passing w0 ≈ w1 and ≈ h0 = h1.
 *
 * @param w0        - Normalised weight of layer 0 (0–1).
 * @param w1        - Normalised weight of layer 1 (0–1).
 * @param h0        - Height-map sample of layer 0 (0–1).
 * @param h1        - Height-map sample of layer 1 (0–1).
 * @param sharpness - Transition sharpness, typically 4–16.
 * @returns Blend factor t in [0, 1]. t=1 → pure layer 0, t=0 → pure layer 1,
 *   t=0.5 → equal blend.
 */
export function heightBlend(
  w0: number,
  w1: number,
  h0: number,
  h1: number,
  sharpness: number,
): number {
  const raw = (w0 - w1) * sharpness + (h0 - h1);
  // smoothstep over [-1, 1] — raw=0 maps to t=0.5 (equal blend), raw≥+1
  // maps to t=1 (layer 0), raw≤-1 maps to t=0 (layer 1).
  const x = Math.max(0, Math.min(1, (raw + 1) * 0.5));
  return x * x * (3 - 2 * x);
}

// ---------------------------------------------------------------------------
// TSL node (GPU)
// ---------------------------------------------------------------------------

/**
 * tslHeightBlend — TSL Fn node for height-aware layer blending.
 *
 * Mirrors `heightBlend` exactly but runs on the GPU as part of a TSL material
 * graph. Will be wired into TerrainPBRMaterial in Task 8.
 *
 * @param w0        - float node: normalised weight of layer 0.
 * @param w1        - float node: normalised weight of layer 1.
 * @param h0        - float node: height-map sample of layer 0.
 * @param h1        - float node: height-map sample of layer 1.
 * @param sharpness - float node: transition sharpness (typically 4–16).
 * @returns float node in [0, 1] — blend factor t.
 */
 
export const tslHeightBlend: any = Fn(([w0, w1, h0, h1, sharpness]: any) => {
  // Mirrors the CPU formulation: centre on raw=0 so equal inputs yield
  // a 50/50 blend. The earlier `clamp(raw, 0, 1)` pinned any
  // non-positive raw to pure layer 1, which made equal-weight equal-
  // height inputs collapse to a single layer.
  const raw = float(w0).sub(float(w1)).mul(float(sharpness)).add(float(h0).sub(float(h1)));
  return smoothstep(float(-1.0), float(1.0), clamp(raw, float(-1.0), float(1.0)));
});
